import { FilePdfFilled, FileTextFilled } from "@ant-design/icons"
import { Avatar, Flex, Tag, Typography } from "antd"
import React from "react"

import { IS_PROD } from "../../../../../constants/dev.ts"
import { getDeclineReasonForDisplay } from "../../../../../utils/documents/document-decline-outcome.ts"
import { DocumentVersionRow } from "../../../../DocumentTemplates/components/DocumentVersionRow.tsx"
import { SendForSignatureButton } from "../../../../Esign/components/SendForSignatureButton.tsx"
import { offersSendForSignature } from "../../../../Esign/helpers/signable-record.ts"
import {
  PROPOSAL_STATUSES,
  PROPOSAL_STATUSES_OBJ
} from "../../../../Proposals/constants/proposal-statuses-keys.ts"
import type {
  LeadDocumentAttachment,
  LeadProposal
} from "../../../types/lead.documents.types.ts"
import { LeadViewProposalItemActions } from "./LeadViewProposalItemActions.tsx"

const { Text, Paragraph } = Typography

interface LeadViewProposalItemProps {
  proposal: LeadProposal
  isUpdateAllowed: boolean
  viewOnly: boolean
  onViewProposal: (proposal: LeadProposal) => void
  onShare: (proposal: LeadProposal) => void
  onDuplicateProposal: (proposal: LeadProposal) => void
  onViewPDF: (attachment: LeadDocumentAttachment) => void
  onMarkAsSent: (proposalId: string) => void
  onSendToReview: (proposalId: string, attachmentId: string) => Promise<void>
}

export function LeadViewProposalItem({
  proposal,
  isUpdateAllowed,
  viewOnly,
  onViewProposal,
  onDuplicateProposal,
  onMarkAsSent,
  onShare,
  onViewPDF,
  onSendToReview
}: LeadViewProposalItemProps) {
  const statusData = PROPOSAL_STATUSES_OBJ?.[proposal?.status]
  const isCancelled = proposal?.status === PROPOSAL_STATUSES.CANCELLED
  const displayReason = getDeclineReasonForDisplay(proposal?.decline_reason)
  const latest_attachment =
    proposal?.latest_attachment || proposal?.attachments?.at(-1)
  const isUploadedFile = latest_attachment?.extra?.uploaded || false

  return (
    <Flex
      vertical
      className={"p-2 rounded-card border border-border"}
      gap={"small"}
    >
      <Flex
        flex={1}
        vertical
        gap={"small"}
        className={"xl:flex-row xl:items-center"}
        justify={"space-between"}
      >
        <Flex align={"center"} wrap gap={"large"} justify={"space-between"}>
          <Flex align={"center"} gap={"middle"}>
            <div
              className={`px-3 flex justify-center align-center aspect-square rounded-lg ${latest_attachment ? "bg-primary/20" : "bg-primary/10"}`}
            >
              {latest_attachment ? (
                <FilePdfFilled className={"text-primary"} />
              ) : (
                <FileTextFilled />
              )}
            </div>

            <div>
              <Flex gap={"small"} align={"center"} className={"mb-0.5"}>
                <Text strong>{proposal?.proposal_id} </Text>
                {!IS_PROD && proposal?.is_version_2 && (
                  <Tag className={"text-bold"} color={"red"}>
                    V2
                  </Tag>
                )}
                <Tag style={statusData?.style}>{statusData?.label}</Tag>
                {typeof proposal?.is_latest_version === "boolean" && (
                  <DocumentVersionRow
                    versionNumber={proposal?.version_number}
                    isLatest={proposal.is_latest_version}
                  />
                )}
              </Flex>
              <Text type="secondary">Created {proposal?.created}</Text>
            </div>
          </Flex>

          <Flex gap={"small"} align={"center"}>
            <Avatar size={28}>{proposal.created_by_name?.[0] ?? "U"}</Avatar>
            <Text className={"text-sm"}>{proposal.created_by_name}</Text>
          </Flex>
        </Flex>

        <Flex gap="small" wrap className="shrink-0">
          {!viewOnly && offersSendForSignature(proposal) && (
            <SendForSignatureButton
              iconOnly
              leadId={proposal.lead_id}
              subjectType="proposal"
              subjectId={proposal.id}
            />
          )}
          <LeadViewProposalItemActions
            proposal={proposal}
            viewOnly={viewOnly}
            isUpdateAllowed={isUpdateAllowed}
            isUploadedFile={isUploadedFile}
            onViewPDF={onViewPDF}
            onDuplicateProposal={onDuplicateProposal}
            onMarkAsSent={onMarkAsSent}
            onShare={onShare}
            onViewProposal={onViewProposal}
            onSendToReview={onSendToReview}
          />
        </Flex>
      </Flex>
      {displayReason ? (
        <Flex vertical>
          <Text strong>{isCancelled ? "Cancellation reason" : "Reason"}</Text>
          <Paragraph
            type={"secondary"}
            ellipsis={{ rows: 3, expandable: true, symbol: "more" }}
          >
            {displayReason}
          </Paragraph>
        </Flex>
      ) : null}
    </Flex>
  )
}
